import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, pagedOrThrow, pageQuery, type Envelope, type PageRequest, type PagedData } from "../api/client";
import type { IFilter, Session } from "../api/models";
import { IN_PLAY_POLL_MS } from "../api/queries";
import { readClaims } from "../auth/token";
import type { ActiveGame } from "../floor/floor-logic";

// THE IN PLAY PAGE'S ENDPOINTS (`in-play.service.ts`, AND THE `players.service.ts` CALLS THE
// ACTIONS SIDEBAR MAKES), VERBATIM. ANGULAR POLLS SESSIONS, ACTIVE GAMES AND THE SELECTED
// SESSION TOGETHER EVERY 15S AND STOPS ALL OF IT WHILE THE ACTIONS SIDEBAR IS OPEN; `paused`
// CARRIES THAT HERE.

const user = () => String(readClaims()?.userId ?? "anonymous");
export const inPlayKey = (...parts: unknown[]) => [user(), "in-play", ...parts];

// `GetPagedSessions`
export function useInPlaySessions(siteId: number | null, filters: IFilter[] | null, page: PageRequest, paused: boolean) {
  return useQuery({
    queryKey: inPlayKey("sessions", siteId ?? -1, filters ?? [], page),
    queryFn: ({ signal }) =>
      apiRequest<Envelope<PagedData<Session>>>(`/ht/paged-sessions?${pageQuery(page, { site_ids: String(siteId) })}`, {
        method: "POST",
        body: filters,
        signal,
      }).then((r) => pagedOrThrow(r.data, "paged-sessions")),
    enabled: siteId !== null && filters !== null,
    // A HIDDEN TAB STOPS POLLING AND REFETCHES ON RETURN (`refetchIntervalInBackground` IS FALSE)
    refetchInterval: paused ? false : IN_PLAY_POLL_MS,
    placeholderData: (previous) => previous,
  });
}

// `GetActiveGames`: THE GAMES IN PLAY UNDER THE SAME FILTER, FOR THE MAP'S COLORED DOTS
export function useActiveGames(siteId: number | null, filters: IFilter[] | null, paused: boolean) {
  return useQuery({
    queryKey: inPlayKey("active-games", siteId ?? -1, filters ?? []),
    queryFn: ({ signal }) =>
      apiRequest<Envelope<ActiveGame[] | null>>(`/ht/active-games?site_ids=${encodeURIComponent(String(siteId))}`, {
        method: "POST",
        body: filters,
        signal,
      }).then((r) => r.data ?? []),
    enabled: siteId !== null && filters !== null,
    refetchInterval: paused ? false : IN_PLAY_POLL_MS,
    placeholderData: (previous) => previous,
  });
}

// `GetActiveSession`: THE SELECTED SESSION, RE-READ ON EACH POLL. IT STARTS FROM THE CARD THAT WAS
// CLICKED AND IS FIRST RE-READ ON THE NEXT TICK, AS ANGULAR DOES. A NULL `data` MEANS IT ENDED.
export function useSelectedSession(session: Session | null, paused: boolean) {
  return useQuery({
    queryKey: inPlayKey("active-session", session?.id ?? null, session?.location ?? null),
    queryFn: ({ signal }) =>
      apiRequest<Envelope<Session | null>>("/ht/get-active-session", {
        method: "POST",
        body: { session_id: session?.id, location: session?.location },
        signal,
      }).then((r) => r.data ?? null),
    enabled: session !== null,
    initialData: session ?? undefined,
    initialDataUpdatedAt: () => Date.now(),
    staleTime: IN_PLAY_POLL_MS,
    refetchInterval: paused ? false : IN_PLAY_POLL_MS,
    retry: false,
  });
}

// `GetPlayerByPlayerId`, READ ONLY FOR THE GREET AND NOTE COUNTS ON THE ACTIONS SIDEBAR
export interface PlayerCounts {
  playerId?: string;
  greetCount?: number;
  noteCount?: number;
}
export function usePlayerCounts(playerId: string | undefined) {
  return useQuery({
    queryKey: inPlayKey("player-counts", playerId ?? ""),
    queryFn: ({ signal }) => apiRequest<Envelope<PlayerCounts | null>>(`/player/${encodeURIComponent(playerId ?? "")}`, { signal }).then((r) => r.data ?? {}),
    enabled: !!playerId,
  });
}

// `CreateGreet`, WITH THE MACHINE LOCATION THE PLAYER WAS GREETED AT
export function useInPlayGreet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (g: { cmsPlayerId: string | undefined; location: string | undefined; message: string; status: string }) =>
      apiRequest<unknown>("/player/create-player-greet", {
        method: "POST",
        body: { cms_player_id: g.cmsPlayerId, egm_floor_location: g.location, message: g.message, status: g.status },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: inPlayKey("player-counts") }),
  });
}

// `CreateNote`
export function useInPlayNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (n: { cmsPlayerId: string | undefined; message: string }) =>
      apiRequest<unknown>("/player/create-player-note", { method: "POST", body: { cms_player_id: n.cmsPlayerId, message: n.message } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: inPlayKey("player-counts") }),
  });
}
